import { reactive, computed } from "vue";

/**
 * useCan — sumber kebenaran tunggal untuk hak akses user di sisi FE.
 *
 * Alur mekanisme:
 *  - `setUser(user)` dipanggil tiap navigasi (lihat router guard). `user`
 *    adalah resp.data dari /user/user_data ({ id, username, name, role, ... }).
 *  - `can(required, opts)` mengevaluasi apakah user sekarang punya hak.
 *  - directive `v-can` (lihat src/directives/can.js) memakai `can()` ini, jadi
 *    semua logika hak akses terpusat di satu tempat.
 */

// ---- konfigurasi (hard-coded, ubah di sini bila perlu) -----------------
// Role yang otomatis lolos semua pengecekan. Kosongkan array kalau tidak
// ingin ada super-role yang bypass.
const SUPER_ROLES = ["ADMINISTRATOR"];
// Properti pada object user yang menampung daftar hak granular (opsional).
// Kalau backend belum mengirim ini, role saja yang dipakai.
const ACCESS_KEY = "access";
// ------------------------------------------------------------------------

const state = reactive({
  user: null,
});

// Set user aktif (panggil dari router guard tiap navigasi).
export function setUser(user) {
  state.user = user || null;
}

// Hapus user aktif (panggil saat logout / token invalid).
export function clearUser() {
  state.user = null;
}

// Kumpulkan semua "grant" milik user: role + daftar access granular.
function grantsOf(user) {
  if (!user) return [];
  const grants = [];
  if (user.role) grants.push(user.role);
  const access = user[ACCESS_KEY];
  if (Array.isArray(access)) grants.push(...access);
  return grants;
}

/**
 * Inti pengecekan hak akses untuk user TERTENTU.
 * Dipakai bersama oleh can() (user aktif) dan checkAccess() (router guard).
 * @param {object|null} user - object user ({ role, access?, ... })
 * @param {string|string[]} required - hak yang dibutuhkan (role / access)
 * @param {{ all?: boolean }} [opts] - all=true butuh SEMUA (AND), default any (OR)
 * @returns {boolean}
 */
export function checkAccess(user, required, opts = {}) {
  if (!user) return false;

  // super role lolos semua pengecekan
  if (SUPER_ROLES.includes(user.role)) return true;

  // tanpa syarat = siapa saja yang sudah login boleh
  if (required == null || required === "") return true;

  const need = Array.isArray(required) ? required : [required];
  const grants = grantsOf(user);

  return opts.all
    ? need.every((r) => grants.includes(r))
    : need.some((r) => grants.includes(r));
}

/**
 * Daftar path yang boleh dibuka user terbatas, atau null kalau user bebas.
 * Sumbernya kolom `user.allowed_paths` di DB (dikirim backend via
 * /user/user_data sebagai array). NULL / kosong = bebas akses semua.
 * Super-role tidak pernah terbatas.
 * @param {object|null} user
 * @returns {string[]|null}
 */
export function restrictedPathsOf(user) {
  if (!user) return null;
  if (SUPER_ROLES.includes(user.role)) return null;
  const paths = user.allowed_paths;
  if (!Array.isArray(paths) || paths.length == 0) return null;
  return paths;
}

// Apakah user aktif ber-akses terbatas (dipakai App.vue untuk menu minimal).
export function isRestricted() {
  return restrictedPathsOf(state.user) != null;
}

// Apakah sidebar ditampilkan untuk user aktif (kolom `user.show_sidebar`
// di DB, default tampil bila belum ada datanya).
export function isSidebarShown() {
  if (!state.user) return true;
  return state.user.show_sidebar != 0;
}

/**
 * Pengecekan hak akses untuk user yang sedang aktif (state.user).
 * @param {string|string[]} required - hak yang dibutuhkan (role / access)
 * @param {{ all?: boolean }} [opts] - all=true butuh SEMUA (AND), default any (OR)
 * @returns {boolean}
 */
export function can(required, opts = {}) {
  return checkAccess(state.user, required, opts);
}

// Kebalikan dari can() — dipakai directive v-can:not.
export function cannot(required, opts = {}) {
  return !can(required, opts);
}

export default function useCan() {
  return {
    user: computed(() => state.user),
    setUser,
    clearUser,
    can,
    cannot,
    checkAccess,
  };
}
